package com.writerslab

val EnStrings = Strings(
    aiNoCredits = "No credits remaining",
    aiPricing = { charsPerCredit -> "1 credit per ${charsPerCredit / 1000}K characters" },
    aiPreviewWhatYouGet = "What you get",
    aiPreviewTimeHint = "Analysis takes about 3 minutes",
    aiPreviewReviewDesc = "A 1500–2000-word analytical review: the central conflict, the dominant themes, the overall impression the manuscript leaves.",
    aiPreviewReviewSample = "To see your book from the outside — what crystallises as its core, what catches, what falls flat, and the tone the reader carries away from the last page.",
    aiPreviewStrengthsDesc = "The book's strengths and weak spots — what works and what needs work, per the reviewer and the beta reader.",
    aiPreviewStrengthsSample = "For example: the techniques that hold attention, and the places where the text sags.",
    aiPreviewCharactersDesc = "Profiles of every key character: their function in the plot, motivation, emotional arc, strengths, and gaps in development.",
    aiPreviewCharactersSample = "To tell which characters actually carry the story and which sit in the background — and where the figures need more movement to feel alive.",
    aiPreviewPlotDesc = "Plot and structure assessment: opening, orientation, pacing, tension, ending. What's working in the plot, where it sags, what hits, what under-delivers.",
    aiPreviewPlotSample = "To see the book's structural skeleton: which scenes pull the story forward, which mark time.",
    aiPreviewRecommendationsDesc = "A concrete edit list: what to cut, what to rearrange, which scenes to rework, where to strengthen voice, where to raise the stakes — all tied to specific chapters.",
    aiPreviewRecommendationsSample = "So you know where to start editing and in what order — instead of staring down the whole manuscript at once.",
    aiPreviewUnresolvedDesc = "A list of open plot threads, abandoned motifs, unfired Chekhov's guns, and characters who quietly vanished along the way.",
    aiPreviewUnresolvedSample = "To find the threads you meant to tie off but didn't — and to decide deliberately: finish them, or cut them out.",
    aiRerun = { a1: Int -> "Rerun for $a1" },
    aiScore = "Overall score",
    aiTitle = "Deep Analysis",
    aiStatusIdle = "Not started",
    aiStatusRunning = "Analyzing…",
    aiStatusDone = "Done",
    aiStatusError = "Error",
    aiReportLoadFailed = "Couldn't load the finished AI report — it's saved and still yours. " +
        "Please reload the page in a minute; there's no need to run the analysis again.",
    aiScoringQuestions = mapOf(
        // Plot
        "plot_inciting_event" to "In one of the chapters of the first third of the book, does an event occur without which the main plot would not begin?",
        "plot_climax_exists" to "Is there a chapter in the book where the main confrontation or final resolution of the central conflict takes place?",
        "plot_ending_follows" to "Is the way the book ends a consequence of character actions in the preceding chapters?",
        "plot_clear_structure" to "Can the book be divided into four parts: introduction to the situation, development of the conflict, main confrontation, resolution?",
        "plot_no_long_sag" to "Does the book avoid sagging stretches — does every span of two or more consecutive chapters move the main plot forward?",
        "plot_tension_growing" to "Are the events in the last third of the book \u2014 heavier in stakes, emotional intensity, or consequences \u2014 than the events in the first third?",
        "plot_central_conflict_clear" to "In the first third of the book, is it clear who (or what) struggles against whom (or what) in the main conflict?",
        "plot_stakes_clear" to "In the first third of the book, is it clear what the protagonist may lose or gain as a result of the conflict?",
        "plot_stakes_grow" to "Over the course of the book, do what the protagonist risks or what they strive for grow \u2014 do new threats or opportunities appear, or does their scale increase?",
        "plot_events_causal" to "Are important events in the book prepared \u2014 with their causes described in earlier chapters, without unexplained coincidences or sudden external forces?",
        "plot_actions_have_consequences" to "Does the book contain at least two instances where an action by the protagonist in one chapter becomes the cause of an event in a later chapter?",
        "plot_beyond_template" to "In the book, is there a plot move or turn that is not typical for this genre?",
        "plot_has_ambiguity" to "In the book, is there an event that can be understood in at least two different ways?",
        // Characters
        "chars_actions_match_description" to "Do the protagonist\u2019s actions match their described character (or are inconsistencies explained by the plot)?",
        "chars_atypical_explained" to "Are unusual character actions explained in the same or adjacent chapter?",
        "chars_internal_conflict" to "Does the protagonist move between opposing internal states over the course of the book (for example, faith and doubt, love and hatred, resolve and fear)?",
        "chars_varied_reactions" to "Does the protagonist react differently in different situations \u2014 showing at least three distinct emotions?",
        "chars_motives_clear" to "Is it clear what the protagonist wants to achieve \u2014 in a way that can be stated in one sentence?",
        "chars_motives_acted_out" to "Is the protagonist\u2019s main desire confirmed by their actions \u2014 do they take at least three actions aimed at achieving this goal?",
        "chars_main_distinct" to "Are the main characters distinguishable by their decisions \u2014 you cannot swap their actions across scenes without losing meaning?",
        "chars_secondary_distinct" to "Do secondary characters have a distinguishing action or line \u2014 not appearing only as background?",
        "chars_protagonist_changes" to "Does the protagonist at the end of the book differ from themselves at the beginning \u2014 have their views, relationships, or goals changed?",
        "chars_change_prepared" to "Is the protagonist\u2019s change prepared \u2014 is there a concrete event in one of the chapters between their initial and final state, after which they began to change?",
        "chars_protagonist_has_flaw" to "Does the protagonist have a real character weakness \u2014 not a trait that the book presents as a virtue or the cause of their victories?",
        "chars_choice_against_self_interest" to "Does the protagonist ever make a decision after which their own position becomes clearly worse and stays that way to the end \u2014 a genuine loss rather than a favorable trade for something greater?",
        "chars_secondary_has_own_goal" to "Does at least one secondary character act out of their own interests or past, rather than from their role beside the protagonist \u2014 having their own line of action across several chapters?",
        "chars_growth_costs" to "Is the protagonist\u2019s change over the course of the book accompanied by losses (relationships, beliefs, abilities, dreams), and not reduced solely to clean acquisitions without regret?",
        "chars_inner_life_revealed" to "Is the protagonist\u2019s inner world shown \u2014 their thoughts, doubts, and feelings, not only their outward actions?",
        "chars_backstory_shapes" to "Does the protagonist have a backstory or past experience that explains who they are and why they act as they do?",
        "chars_antagonist_dimensional" to "Does the antagonist (or the main opposing force) have their own understandable motivation, rather than only embodying evil?",
        "chars_motivation_rooted" to "Do the protagonist\u2019s desires and goals stem from their character, values, or past, rather than merely being asserted for the sake of the plot?",
        "chars_evokes_investment" to "Does the protagonist evoke emotional investment \u2014 a wish to sympathize with them, worry for them, or admire them?",
        "chars_appearance_visualizable" to "Is the protagonist\u2019s appearance described enough to picture them visually?",
        "chars_shown_not_told" to "Is the protagonist’s character revealed through their actions and reactions rather than through direct authorial labels?",
        "chars_voice_distinctive" to "Does the protagonist have a recognizable, distinct manner of speech and behavior that sets them apart from other characters?",
        "chars_dialogue_alive" to "Does the protagonist’s dialogue sound like living speech rather than a dry exchange of information?",
        "chars_sensory_concrete" to "Is the protagonist rendered tangibly — through details, gestures, and sensations, not just general statements?",
        // Language
        "lang_sentence_length_varies" to "Does sentence length in the text vary \u2014 are there both short (≤8 words) and long (≥15 words) sentences?",
        "lang_sentence_structure_varied" to "Is sentence structure varied \u2014 does a significant share of sentences use complex constructions (with conjunctions or detached phrases)?",
        "lang_no_tautology" to "Is the text largely free of tautologies (same-root repetition in adjacent words) and pleonasms (redundant combinations like \u201Cfree vacancy\u201D, \u201Cfall down\u201D)?",
        "lang_reads_first_time" to "Are sentences in the text understandable on first reading \u2014 are phrases that require re-reading scarce?",
        "lang_no_clumsy_phrases" to "Does the text read naturally \u2014 are phrases with awkward word order or unnatural construction scarce?",
        "lang_imagery_not_cliche" to "Are typical genre cliche images (“eyes like stars”, “heart shattered to pieces”) rare in the text?",
        "lang_register_fits_genre" to "Does the speech style suit fiction prose (the dominant register is not formal-bureaucratic)?",
        "lang_register_switches_intentionally" to "Does the speech style switch intentionally between scenes of different types (for example, between description and dialogue)?",
        "lang_no_info_dump" to "Are info-dump lines in dialogue \u2014 long explanations whose sole purpose is to convey background information needed by the reader (biographies, past events, how the world or situation works, professional knowledge) rather than responding to the interlocutor \u2014 rare in the text?",
        "lang_has_subtext" to "Does the text regularly contain lines or actions where what is implied clearly differs from what is said or done directly?",
        "lang_distinct_voice" to "Does the prose have a recognizable authorial voice \u2014 a tone and manner that distinguish this author from typical genre fiction?",
        "lang_atmosphere_works" to "Does the text create a sustained mood or atmosphere through its combination of details, rhythm, and word choice?",
        "lang_dialogue_alive" to "Do the dialogues in the text sound like living speech \u2014 do different characters speak differently rather than in the author\u2019s single voice?",
        "lang_humor_works" to "Does the text contain successful jokes, ironic observations, or wordplay that actually land?",
        "lang_emotional_range" to "Does the text show a range of emotional registers \u2014 transitions between, for example, irony and seriousness, sadness and lightness?",
        // First impression
        "plot_hook_first_part" to "In the first part of the book, is there an event or situation that leaves the reader with an unresolved question?",
        "plot_orientation_clear" to "In the first part of the book, are the protagonist, place of action, and time of action named?",
        "plot_confusion_creates_intrigue" to "Does the book contain moments that are initially unclear to the reader but later receive an explanation in one of the subsequent chapters?",
        "plot_balance_fits_genre" to "Does the ratio of descriptions, action, and dialogue in the book suit the specified genre?",
        "plot_ending_fits_genre" to "Does the ending of the book meet the expectations of the specified genre (for a romance \u2014 the union of the heroes; for a detective story \u2014 the solution of the crime; for a tragedy \u2014 catharsis)?",
        "plot_climax_earned" to "Does the protagonist achieve the result at the climax through an action with a real cost to them \u2014 not as a reward for previously accumulated advantages, a lucky coincidence, or external help?",
        "plot_costly_choices" to "Does at least one of the protagonist\u2019s decisions lead to a consequence that is undesirable for them and persists to the end of the book \u2014 do they suffer from it or regret it, rather than receiving something greater in return?",
        "plot_theme_present" to "Does the book have a discernible recurring theme \u2014 a statement about people, the world, or life beyond the plot itself (for example, \u201Cthe price of a choice\u201D, \u201Cloneliness\u201D, \u201Caccepting loss\u201D) that the events of the book consistently illustrate?",
        "plot_relationships_evolve" to "Does at least one named pair of characters\u2019 relationship qualitatively change over the course of the book \u2014 the initial state (trust, enmity, distance, love, indifference) becomes principally different by the end, and the transition is shown through events rather than declared in a single line?",
        "plot_reader_learns" to "By the end of the book, does the reader come to understand something about the world, situation, or people that was not clear in the first third \u2014 that is, a new understanding, not merely a new event?",
    ),
    aiCategoryStats = { yes, applicable, numeric ->
        val score = numeric?.let { " · ${(it * 10).toInt() / 10.0}/10" } ?: ""
        "$yes of $applicable \u201Cyes\u201D$score"
    },
    aiUnlock = { a1: Int -> "Get the deep breakdown · $a1" },
    aiUnlockFree = "Get the deep breakdown for free",
    aiUnlockFreeAfterSignIn = "Sign in — get the breakdown for free",
    aiSidebarRun = { a1: Int -> "Run · $a1" },
    aiSidebarSignIn = "Sign in",
    aiFreeShortBadge = { a1: Int -> "Free (${a1}/5 left)" },
    aiFreeShortBadgeAnon = "Free after sign-in",
    aiFreeShortHint = { a1: Int -> "Short text — free. ${a1}/5 runs left today" },
    aiFreeShortHintAnon = "Short text — sign in for 5 free deep-analysis runs per day",
    aiFreeShortQuotaAnon = { chars ->
        "Texts under $chars characters — 5 free runs per day after signing in"
    },
    aiFreeShortQuotaRemaining = { remaining, chars ->
        "Free — $remaining of 5 short-text (under $chars characters) runs left today"
    },
    aiFreeShortQuotaExhausted = { chars ->
        "Today's 5 free short-text (under $chars characters) runs are used up"
    },
    analysisFailed = "Analysis failed",
    analyzing = { a1: String -> "Analyzing ${a1}..." },
    appName = "Writers Lab",
    arcPrefix = "Arc: ",
    change = "Change",
    charactersLabel = "Characters",
    charsLabel = "Characters",
    close = "Close",
    credits = "credits",
    detailedMetrics = "Detailed metrics",
    blockStatic = "Static analysis",
    blockManuscript = "Editing",
    blockAi = "Deep analysis",
    blockPromo = "Promo materials",
    blockStaticSub = "Text form by the numbers: sentence rhythm, dialogue, sentence length, vocabulary richness — without reading for meaning.",
    blockManuscriptSub = "Stylistic issues highlighted right in the manuscript text.",
    blockAiSub = "The whole book analysed: plot, characters, dialogue, problem scenes, unresolved threads.",
    blockPromoSub = "Materials to sell the book: annotation, covers, video teaser.",
    detailedMetricsExplain = "This is not a neural network — it is strict language statistics: the algorithm reads the shape of the text (sentence rhythm, dialogue density, sentence length, vocabulary richness) without ever caring what the book is about.\n\nThe target values for each metric aren't drawn from any literary canon or critics' shortlist. They are averages from the books that resonate best with mass readers on the marketplaces — a record of which textual shapes tend to draw the highest reader ratings.",
    blockKindStatic = "Text statistics",
    blockKindAi = "Deep analysis",
    helpQualityTitle = "Overall quality score",
    helpQualityBody = "A summary score from 0 to 10 — how close the shape of the text sits to what mass readers tend to enjoy.\n\nComputed from dozens of statistical metrics: sentence rhythm, dialogue density, vocabulary, clarity and so on. Target values are drawn from books that consistently earn high reader ratings on the marketplaces.\n\nThere is no AI involved here — this is a formal analysis of form, not meaning. Next to the score you can also see length, reading time, point of view, and the number of characters.\n\nThe score is designed for a single coherent work of any length. Story collections and books made of several unrelated novellas average their metrics together and produce a mixed result.",
    helpHighlightsTitle = "Strengths and weak spots",
    helpHighlightsBody = "The two metrics pulling the overall score up the most, and the two pulling it down hardest.\n\nA short summary of the detailed metrics block — a way to see at a glance what works and what hurts the score without expanding the full list.",
    helpAiScoreTitle = "Deep-review score",
    helpAiScoreBody = "The headline result of the deep review: an overall 0–10 score plus a breakdown by key dimensions — plot, characters, language.\n\nEach category comes from yes/no judgments against dozens of craft criteria (a clear inciting event, believable motivations, sentence clarity, etc.). A score of 10 is awarded once at least 85% of the criteria are satisfied — a small allowance because perfection isn't realistic.",
    helpBookProfileTitle = "Book profile",
    helpBookProfileBody = "The book's mood and emotional makeup: dominant mood, the share of the eight basic emotions (joy, sadness, fear, anger and so on), and automatically picked genre tags.\n\nEverything is derived from the text itself — which words appear and in what proportions. This is a description, not a quality score: it helps you quickly understand what kind of book this is.",
    helpPromoSettingsTitle = "Title and author",
    helpPromoSettingsBody = "These fields are used by the cover and teaser-video generators. Fill them in once here — both generators will pick the values up, no need to re-type them.",
    helpMetricsTitle = "Detailed metrics",
    helpMetricsBody = "The full breakdown of statistical indicators behind the overall quality score: rhythm, dialogue, vocabulary, clarity, imagery, and so on.\n\nGreen highlight — the metric is pulling the score up. Red — pulling it down. Every metric has a tooltip with the target values for text that lands well with mass readers.\n\nThis is a formal analysis without comprehension: the algorithm reads form, not meaning.",
    helpPromoMaterialsTitle = "Promo materials",
    helpPromoMaterialsBody = "Ready-to-publish materials for the book: a short blurb, cover variants, and a video teaser.\n\nEverything is built on the analysis result: the blurb leans on the book's strengths, the cover and teaser pick a visual feel that matches mood and genre.\n\nBlurb and cover take seconds; the video teaser takes a couple of minutes (two steps — pick a frame, then animate it).",
    helpAiInsightsTitle = "Deep review: full report",
    helpAiInsightsBody = "The full text of the book's review after a complete read. The report has several tabs:\n\n• Plot — structure, pacing, conflicts, stakes, cause-and-effect.\n• Characters — motivations, arcs, distinct voices.\n• Language — clarity, imagery, register, sentence rhythm.\n• Recommendations — what to strengthen first.\n• Review — a reader's take.\n• Loose ends — story threads opened but never closed.\n\nThe report ends with a table of criteria used to compute the scores.",
    eulaPrefix = "I agree to the",
    eulaLink = "User Agreement",
    privacyLink = "Privacy Policy",
    metaTitle = "Writers Lab — book text analysis and AI review",
    metaDescription = "Upload your manuscript or book — Writers Lab breaks down style, pacing, dialogue, characters, and highlights strengths and weaknesses. AI report on demand.",
    metaLocale = "en_US",
    balanceLabel = "Balance",
    packsHeading = "Top up balance",
    packStartName = "Starter",
    packMediumName = "Pack",
    packLargeName = "Big pack",
    packPerBook = "per book",
    packDiscount = { pct -> "−$pct%" },
    packCreditsCount = { n -> if (n == 1) "$n credit" else "$n credits" },
    packCheckoutStub = "International payments aren't enabled yet — please contact support",
    paymentSuccess = { n -> "Payment confirmed — $n credits added" },
    paymentCanceled = "Payment canceled",
    checkoutRedirecting = "Opening the payment page…",
    paymentWaitingBanner = "Waiting for payment confirmation. If you've already paid, the credits will appear on their own.",
    paymentResume = "Back to payment",
    paymentError = "Could not start checkout. Please try again later",
    regionRussia = "Russia",
    regionInternational = "Rest of world",
    weakMetricsHint = "The red-outlined metrics are the ones dragging the overall score down the most — start there.",
    supportContact = "Contact support",
    supportEmailSubject = "Question about Writers Lab",
    supportEmailBody = "Hi!\n\n",
    emotionAnger = "anger",
    emotionAnticipation = "anticipation",
    emotionDisgust = "disgust",
    emotionFear = "fear",
    emotionJoy = "joy",
    emotionSadness = "sadness",
    emotionSurprise = "surprise",
    emotionTrust = "trust",
    footerHint = "Part of what2read — a family of book tools",
    footerPowered = "© what2read",
    heroesMany = "many",
    heroesNone = "—",
    heroesOne = "one",
    heroesSeveral = "several",
    heroesVeryMany = "very many",
    keyStrengths = "Key strengths",
    keyWeaknesses = "Key weaknesses",
    weaknesses = "Weaknesses",
    promoSettingsLabel = "For promo materials",
    replaceBook = "Replace book",
    unresolvedEmptyTitle = "Every thread tied",
    unresolvedEmptyHint = "Plot lines close, set-ups pay off, Chekhov's guns fire. No abandoned motifs were found in your text — a rare result; most manuscripts leave something dangling.",
    weakSpots = "Weak spots",
    readingTimeLabel = "Reading",
    hoursShort = "h",
    minutesShort = "min",
    landingExampleAnnotationHook = "An English gentleman wagers half his fortune that he can circle the globe in eighty days.",
    landingExampleAnnotationLabel = "Blurb",
    landingExampleAnnotationText = "Phileas Fogg is a London gentleman so precise his neighbours set their watches by him. He lives by a timetable, plays cards at the same club, and tolerates no slip from his servants. Then one evening he stakes his entire fortune — twenty thousand pounds — that he will travel around the world in eighty days. A pursuit follows him at once — quiet, methodical, unrelenting. Ahead lie India's impassable jungles and foreign laws, Japan, the American prairies with their bison and tribes, the stormy Atlantic. Every delay costs hours, every hour costs money, every mistake could cost everything.",
    landingExampleReviewLabel = "Review",
    landingExampleReviewText = "The novel, built around the travels of the enigmatic Phileas Fogg, weaves adventure, intrigue and cultural detail into a brisk plot that pulls the reader along. Fogg's wager — to circle the Earth in 80 days — becomes more than a race against the clock; it serves as the novel's controlling metaphor for confronting obstacles, including police pursuit, cultural friction and personal trials. The book's chief strength is its capacity to build tension through a chain of trials, each one revealing the characters' motivations.",
    landingHeadline = "From manuscript to a finished book",
    landingSubheadline = "A helper for working on fiction. We support the author at every stage — from breaking down the manuscript to the cover — and give a calm outside look at your prose.",
    landingFeaturesTitle = "Everything an author needs, in one place",
    landingFeaturesTextTitle = "Work on the text",
    landingFeaturesTextItems = listOf(
        "A review like a critic and a beta reader" to "plot, characters, language, strengths and weak spots, concrete recommendations.",
        "Weak spots — right in the text" to "clichés, bureaucratese, run-ons, weak verbs. You see what to rewrite and where.",
        "Text profile" to "pace, rhythm, dialogue, readability — and how close it sits to what a wide audience tends to enjoy.",
    ),
    landingFeaturesPubTitle = "Get ready to publish (incl. self-publishing)",
    landingFeaturesPubItems = listOf(
        "Blurb" to "several variants to hook the reader.",
        "Cover" to "a finished cover for your book.",
        "Teaser video" to "a short clip from your book's scenes — for social media.",
    ),
    manuscriptTitle = "Manuscript text",
    manuscriptHint = "Preview: this is how your manuscript will read. Flagged stylistic issues will be highlighted here.",
    hlGroupCliche = "Clichés",
    hlGroupFilter = "Filter words",
    hlGroupBureaucratese = "Bureaucratese",
    hlGroupFiller = "Filler words",
    hlGroupBegan = "Indirect actions",
    hlGroupTautology = "Pleonasms",
    hlGroupWeakSpeech = "Speech-tag adverbs",
    hlGroupAdjStack = "Adjective stacking",
    hlGroupAuthorStance = "Authorial intrusion",
    hlGroupIntensifier = "Intensifiers",
    hlGroupWeakVerb = "Weak verbs",
    hlGroupPassive = "Passive voice",
    hlHintCliche = "a worn-out image — readers skim past it and no picture forms. Fix: replace with a fresh, concrete detail, or cut it — \"a deafening silence fell\" → \"no one dared to clink a spoon\".",
    hlHintFilter = "filters the event through the character's perception (\"saw that…\") and distances the reader. Fix: show it directly — \"he heard the door slam\" → \"the door slammed\".",
    hlHintFiller = "a word that carries no meaning. Fix: delete it — the sentence rarely suffers: \"he was, well, a pretty ordinary guy\" → \"he was an ordinary guy\".",
    hlHintBureaucratese = "officialese instead of a live verb. Fix: use a plain verb (\"conduct an inspection\" → \"inspect\").",
    hlHintBegan = "\"began/started to do\" instead of the action itself. Fix: drop began/started — \"he began to climb the stairs\" → \"he climbed the stairs\".",
    hlHintTautology = "a pleonasm — a word whose meaning the neighbouring one already carries. Fix: drop the redundant one — \"nodded his head\" → \"nodded\", \"the very best\" → \"the best\".",
    hlHintWeakSpeech = "an adverb propping up a weak speech verb. Fix: carry the tone with action or a stronger verb — \"said sharply\" → \"snapped\".",
    hlHintAdjStack = "three or more modifiers in a row blur the image. Fix: keep the one or two sharpest — \"a tall, thin, pale stranger\" → \"a thin, pale stranger\".",
    hlHintAuthorStance = "a direct judgement or aside to the reader. Fix: let the reader draw the conclusion — \"of course, he was a scoundrel\" → \"he took the money and didn't look back\".",
    hlHintIntensifier = "pumps up a weak word instead of a precise one. Fix: pick a stronger word (\"very big\" → \"huge\").",
    hlHintWeakVerb = "a state verb, not an action — be, stand, sit, lie, be located: they say someone simply exists or is positioned somewhere, not what they do, so they read as static. Fix: where it fits, swap in an action verb — \"stood by the window\" → \"froze by the window\", \"was glad\" → \"beamed\".",
    hlHintPassive = "passive voice — an action with no doer, weaker and more impersonal than the direct form. Fix: make it active — \"the door was opened by the wind\" → \"the wind flung the door open\", \"it was decided\" → \"we decided\".",
    hlPrevIssue = "Previous",
    hlNextIssue = "Next",
    hlModeStatic = "Static",
    hlAiIntro = "Smart stylistic-issue search",
    hlAiLoading = "Analysing the first 30,000 characters…",
    hlAiError = "Couldn't analyse. Please try again.",
    hlAiEmpty = "No issues found.",
    hlAiFullLoading = "Scanning the whole manuscript — this can take a few minutes…",
    hlAiBatchNote = { at -> "Style analysis of the whole manuscript started at $at. It's a slow check: anywhere from a few minutes to an hour. You can close the page — the result will be saved." },
    hlAiPreview = { free -> if (free != null && free > 0) "First 30k characters — free" else "First 30k characters — 1" },
    hlAiFull = { cost -> "Whole manuscript — $cost" },
    hlAiSignInToRun = "Sign in to run a scan",
    hlAiFullRate = { cost -> "Full scan — $cost" },
    hlAiFullRateUnit = "per 100k characters",
    hlScrollHintMouse = "The wheel scrolls the page · click the text to scroll it",
    hlScrollHintTouch = "Scroll the text with two fingers",
    manuscriptLoading = "Loading the text…",
    manuscriptUnavailable = "Couldn't load the manuscript text.",
    msEditLockedHint = "Text editing becomes available after your first credit purchase.",
    msSave = "Save",
    msSaving = "Saving…",
    msCancelEdit = "Cancel",
    msCancelConfirm = "Discard changes?",
    msEditedCount = { n -> "Paragraphs changed: $n" },
    msSaved = { dropped -> if (dropped > 0) "Saved. Smart findings removed: $dropped." else "Saved." },
    msSavedReloadPage = "Saved, but the text on screen didn't refresh — reload the page to keep editing.",
    msSaveTextChanged = "This book's text was changed in another tab. Reload the page — your edits stay in the draft.",
    msSaveScanRunning = "A smart check of this book is running. You can save once it finishes — your edits won't be lost.",
    msSaveBusy = "The book is busy right now. Try saving again in a few seconds.",
    msSaveTooShort = { words -> "The text must keep at least 500 words; it has $words." },
    msSaveFailed = "Couldn't save. Try again — your edits are still here.",
    msEditedAt = { date -> "Text edited $date" },
    msDownloadTxt = "Download",
    msUncheckedNote = "Paragraphs with a violet bar were changed after the smart check — their findings aren't re-checked yet. «Check edits» re-checks them.",
    msDraftFound = "There are unsaved edits for this book.",
    msDraftFoundStale = "There are unsaved edits for this book made before it was last saved. Restoring them replaces the current text.",
    msDraftRestore = "Restore",
    msDraftDiscard = "Discard",
    msDone = "Done",
    msRecheck = "Check edits",
    msRechecking = "Checking…",
    msRecheckFree = "free",
    msRecheckFreeSlot = "free check",
    msRecheckNeedsScan = "needs a smart check",
    msRecheckNeedsScanNote = "Checking edits works on top of a smart check. Run it below the text — after that, edits can be checked on their own.",
    msRecheckNoChanges = "no changes",
    msRecheckNoChangesNote = "Nothing has changed yet. Edit the text and save — only the changed places get checked.",
    msRecheckPriceLoading = "Working out the price of the check — press again in a couple of seconds.",
    msRechecked = { removed, added -> "Edits checked: $removed findings gone, $added new." },
    msRecheckPriceChanged = "The price of the check changed — it's on the button. Press again if that's fine.",
    msRecheckFailed = "Couldn't check the edits. If credits were taken, they'll come back.",
    msRecheckRunning = "Checking the changed places — from half a minute to a few minutes.",
    msLockTitle = "Edit right in the text",
    msLockFix = "fix the highlighted places right here",
    msLockRecheck = "re-check only what you changed",
    msLockDownload = "download the corrected text",
    msLockPrice = { price -> "Opens after your first credit purchase — from $price" },
    msLockChoosePack = "Choose a pack",
    msLockSignIn = "Sign in",
    msLockLater = "Not now",
    aiTextChanged = { date -> "This analysis was made before the text was edited" + (date?.let { " ($it)" } ?: "") + ". Rerun it to take the edits into account." },
    landingFaqTitle = "Frequently asked questions",
    landingFaq = listOf(
        "Will my text leak?" to """
            No — and it would actually work against us. We make money by helping authors, not from their manuscripts, and leaking someone's text would mean the end of the business: the writing community is small, and a reputation there can't be rebuilt.

            What we guarantee specifically:

            - your text is never used to train neural networks;
            - all copyright stays entirely with you;
            - we don't share manuscripts with third parties;
            - you can delete your manuscript at any time — just message support;
            - team access to texts is restricted and covered by an NDA.

            All of this is fixed legally in the Terms of Service.
        """.trimIndent(),
        "Why use this if I can just paste my text into ChatGPT?" to """
            - **Length.** We handle texts of any size, and the length of the book doesn't affect the quality of the analysis.
            - **Consistency.** A regular chatbot returns a different answer every time you rerun the same prompt — our result is reproducible.
            - **Objectivity.** Any model's answer is shaped by the prompt, and working on your own you inevitably tune it toward the answer you want. We evaluate every text the same way.
            - **Method.** This isn't a «pretend you're a critic» prompt. The review is built on dozens of formal questions about the text, drawn from a large body of literary-studies work.
        """.trimIndent(),
        "Can a machine really grasp the full depth of literature?" to """
            You're right: a machine won't replace a human reviewer — and that's not our goal. We give the author a tool to get the most objective feedback possible, quickly and without the stress, revise the manuscript, and only then show it to critics and readers.

            We all know how unpleasant harsh feedback from people can be. And undeserved praise can be even worse. Our response is always constructive, and we try to point out flaws gently — to spare your nerves while still being useful.
        """.trimIndent(),
        "Can the service replace an editor or beta readers?" to """
            No — and that's not its goal. Word processors have long underlined mistakes, but that helps an editor, it doesn't replace one. It's the same here: the service helps the author prepare a manuscript — spot the weak spots and revise — before showing it to real readers, an editor, or a critic. It doesn't replace a human.
        """.trimIndent(),
        "The service rated my book low. Does that mean it's bad?" to """
            No. The service doesn't grade your book or judge its quality — it surfaces characteristics of the text: pace, momentum, structure, rhythm, and more. A low value on one of them isn't a verdict on the book, just a reason to take a closer look at that particular aspect. What to do about it — if anything — is entirely up to you.
        """.trimIndent(),
        "What if my novel is complex, philosophical, or experimental?" to """
            The service works with any prose — complex, philosophical, experimental. It doesn't judge artistic merit or decide whether a book is «good» or «bad». It shows how the text is built: its pace, structure, momentum — and that's useful for any novel, however unconventional. These characteristics help you see where the text carries the reader along and where it risks losing them. Whether to act on that or deliberately leave it as is — that's your call.
        """.trimIndent(),
        "Will you rewrite my book?" to """
            No.

            We don't write the book for you, and we don't force your style into a template. We show how the text is built and how it comes across, so the creative decisions stay yours.
        """.trimIndent(),
        "Will the blurb come out generic?" to """
            No.

            We generate several variants based on the book's content and character. A blurb's job isn't to retell the plot — it's to make a future reader want to open the book.
        """.trimIndent(),
        "Why analyse rhythm and pace at all?" to """
            How a book reads depends on more than its plot.

            Rhythm, sentence length, the amount of dialogue, and emotional intensity all shape how the text is perceived. The analysis lets you see these traits from the outside — the kind of thing that's hard to notice after a long time spent on a manuscript.
        """.trimIndent(),
    ),
    loadingMessages = listOf(
        "Waking the Muse...",
        "Brewing tea...",
        "Looking for the bookmark...",
        "Counting ellipses...",
        "Checking punctuation...",
        "Flipping pages...",
        "Rereading the first page...",
        "Figuring out who's related to whom...",
        "Reconstructing the timeline...",
        "Finding where it all went wrong...",
        "Clarifying character motivations...",
        "Determining who the villain is...",
        "Counting character deaths...",
        "Establishing who loves whom...",
        "Weighing the metaphors...",
        "Sorting epithets by intensity...",
        "Counting em dashes...",
        "Measuring the length of digressions...",
        "Counting the word \"suddenly\"...",
        "Counting adverbs...",
        "Analyzing the unnecessary paragraph...",
        "Predicting where readers give up...",
        "Estimating odds of a happy ending...",
        "Guessing if the movie will be better...",
        "Counting the protagonist's suffering...",
        "Deciding if it was worth it...",
        "Finding symbolism in the weather...",
        "Locating the author's favorite word...",
        "Calculating the dialogue ratio...",
        "Checking how chapters end...",
        "Counting flashbacks...",
        "Evaluating the love interest...",
        "Looking for Chekhov's gun...",
        "Checking if it fired...",
        "Determining narrative pace...",
        "Measuring sentence length...",
        "Counting characters with the same name...",
        "Looking for plot holes...",
        "Pretending we didn't find any...",
        "Cataloguing food descriptions...",
        "Evaluating the villain's credibility...",
        "Finding the author's favorite color...",
        "Counting convenient coincidences...",
        "Hunting for hidden references...",
        "Assessing the protagonist's likability...",
        "Searching for forgotten characters...",
        "Decoding chapter titles...",
        "Measuring action vs. introspection...",
        "Finding the moment readers guess the ending...",
        "Counting meaningful glances...",
        "Counting stares out of windows...",
        "Evaluating character psychology...",
        "Determining the actual genre...",
        "Finding where the author got tired...",
        "Looking for a genuinely happy character...",
        "Counting moon references...",
        "Reconstructing the map...",
        "Figuring out whose story this really is...",
        "Checking if the title makes sense...",
        "Locating the author's personal opinions...",
        "Analyzing character dynamics...",
        "Finding underserved side characters...",
        "Removing the hero to see what's left...",
        "Checking the cardinal directions...",
        "Counting pregnant pauses...",
        "Determining the tone of the final page...",
        "Figuring out why that ending...",
    ),
    metricArcVolatility = "Mood swings",
    metricAuthorStance = "Author stance",
    metricCefrRange = "CEFR range",
    metricCleanSyntax = "Clean syntax",
    metricCoherence = "Coherence",
    metricComplexity = "Complexity",
    metricConcreteness = "Concreteness",
    metricDialogue = "Dialogue",
    metricDirectPov = "Direct narration",
    metricDirectness = "Directness",
    metricExpressiveness = "Expression",
    metricFillers = "Clean speech",
    metricLiveliness = "Liveliness",
    metricMorality = "Morality",
    metricNoNominalisation = "No nominalisation",
    metricNoRepetition = "No repetition",
    metricOriginality = "Originality",
    metricPace = "Pace",
    metricPathos = "Pathos",
    metricPhrasalVerbs = "Phrasal verbs",
    metricPrecision = "Precision",
    metricRareVocabulary = "Rare vocabulary",
    metricRegisterShift = "Register shift",
    metricRhythm = "Rhythm",
    metricSensory = "Sensory",
    metricStageDirections = "Stage directions",
    metricVerbStrength = "Verb strength",
    metricVividness = "Vividness",
    metricVocabulary = "Vocabulary",
    metricVocabularyAge = "Vocabulary age",
    metricVoices = "Voices",
    moodBright = "bright",
    moodDark = "dark",
    moodHeavy = "heavy",
    moodLabel = "Mood",
    moodNeutral = "neutral",
    moodRatherBright = "rather bright",
    moodRatherDark = "rather dark",
    povFirst = "1st person",
    povLabel = "Point of view",
    povMixed = "mixed",
    povThird = "3rd person",
    povUnknown = "—",
    previousBooks = "Previous analyses",
    historyEmptyTitle = "Your books will appear here",
    historyEmptyHint = "Upload your first one — the analysis is saved to your history",
    badgeDeep = "Review",
    badgeSmart = "Style check",
    badgeDeepTip = "Deep analysis is ready for this book",
    badgeSmartTip = "Stylistic issues are highlighted in the text",
    retryButton = "Try again",
    menuAria = "Menu",
    qualityHint = "Predicts how readers typically rate texts with a similar style",
    qualityCaveat = "The score can miss the mark. It's calibrated on contemporary fiction that resonates with mass readers — so translated texts, books from earlier eras, and non-fiction prose tend to score lower than they ought to.",
    redeemApply = "Redeem",
    redeemCodeHint = "Enter redemption code",
    redeemErrorFormat = "Invalid code format",
    redeemErrorNetwork = "Could not reach the server",
    redeemErrorUsed = "Code is invalid or already used",
    redeemSuccess = { a1: Int -> "+${a1} credits added" },
    relationships = "Relationships",
    sectionNarrative = "Narrative character",
    sectionNarrativeHint = "Depends on genre and author's intent",
    sectionProfile = "Profile",
    sectionProfileHint = "Descriptive — not good or bad, depends on genre",
    sectionStyle = "Writing quality",
    sectionStyleHint = "Style guide compliance",
    sectionStyleHintUnreliable = "Style cleanliness — formal craft markers",
    shortTextWarningModerate = "The static analysis below uses statistical metrics and needs 30,000+ words (about a full novel) for reliable style scores. Current scores may be unreliable. The deep analysis works correctly at any length.",
    shortTextWarningSevere = "The static analysis below needs at least 5,000 words for basic metrics and 30,000+ for style metrics. On a text this short, its results may be close to random. The deep analysis works correctly at any length.",
    signIn = "Sign in",
    signOut = "Sign out",
    storyAndCharacters = "Story & Characters",
    strengths = "Strengths",
    tabPlot = "Plot",
    characterAssessment = "Character assessment",
    plotStructure = "Structure",
    plotPacing = "Pacing",
    plotConflict = "Conflict",
    plotStakes = "Stakes progression",
    plotCausality = "Causality",
    charConsistency = "Personality vs. actions consistency",
    charDepth = "Depth",
    charMotivation = "Motivation",
    charDistinctness = "Distinctness",
    charArc = "Character arcs",
    tabLanguage = "Language",
    aiPreviewLanguageDesc = "Prose quality assessment: sentence craft, vocabulary, clarity, imagery, register. The things pure statistics can't tell apart — competent prose vs. clunky, fresh vs. cliché — grounded in passages from your own text.",
    aiPreviewLanguageSample = "Sample: «Sentence variety is real — short nervy clauses in the chase scene give way to longer, contemplative passages in the reflection chapter. Vocabulary is precise without showing off…»",
    languageSentenceCraft = "Sentence craft & rhythm",
    languageVocabulary = "Vocabulary",
    languageClarity = "Clarity & readability",
    languageImagery = "Imagery & metaphor",
    languageRegister = "Register & style",
    tabCharacters = "Characters",
    tabRecommendations = "Recommendations",
    tabReview = "Review",
    tabStrengths = "+/−",
    tabUnresolved = "Unresolved",
    promoMaterialsTitle = "Promo materials",
    promoMaterialsHintReady = "Generate materials to present the book",
    promoMaterialsHintLocked = "Run the deep analysis first",
    promoAnnotationTitle = "Annotation",
    promoCoverTitle = "Cover",
    promoCoverTitleLabel = "Book title",
    promoCoverAuthorLabel = "Author (optional)",
    promoCoverVariantPrefix = "Variant",
    promoCoverGenerateAll = "Generate covers",
    promoCoverRegenerateAll = "Regenerate all",
    promoCoverRegenerateOne = "Regenerate this one",
    promoCoverDownloadOne = "Download",
    promoCoverDownloadAll = "Download all",
    promoCoverTitleRequired = "Enter the book title",
    promoCoverTextModeLabel = "Title on the cover",
    promoCoverTextModeOverlay = "Vector overlay",
    promoCoverTextModeBaked = "Baked into image",
    promoVideoTitle = "Promo video",
    promoGenerateButton = "Generate",
    promoRegenerateButton = "Regenerate",
    promoGeneratingLabel = "Generating…",
    promoTeaserPreviewButton = "Pick a scene",
    promoTeaserPickAnotherScene = "Try a different scene",
    promoTeaserRenderVideoButton = "Render video",
    promoTeaserDownloadButton = "Download video",
    promoTeaserLoadingPreview = "Picking a scene and rendering keyframe… (~30 sec)",
    promoTeaserLoadingVideo = "Rendering video from keyframe… (1-2 min wait)",
    promoTeaserLoadingRegenerate = "Picking a different scene… (~30 sec)",
    promoTeaserSceneLabel = "Scene",
    promoTeaserErrorCopyright = "Couldn't animate this scene — the copyright " +
        "filter blocked it. This commonly happens with well-known franchises " +
        "and recognizable characters. Try a different scene or change the " +
        "book title.",
    promoTeaserErrorPrivacyFace = "The video model rejected the keyframe " +
        "because it contains a recognizable face. Try regenerating the scene " +
        "(different character or angle) or use full quality — its filter is " +
        "more permissive.",
    promoAnnotationPlaceholderHeading = "An annotation is your book's storefront",
    promoAnnotationPlaceholderBody = "Readers spend five seconds deciding whether to " +
        "open a book or walk past it. Authors find this the hardest piece to write — " +
        "they're too close to their own story to spot the sharpest hook. Our generator " +
        "draws on copywriting and neuromarketing research: precise hook, clear stakes, " +
        "a cliffhanger ending — every detail tuned to make the book open.",
    promoAnnotationDownloadButton = "Download",
    priceTag = { n -> if (n == 0) " · free" else " · $n" },
    priceUnits = { n -> "$n cr" },
    promoTeaserStepsHeading = "The teaser is generated in two steps:",
    promoTeaserStep1Title = "Pick a scene and render the keyframe",
    promoTeaserStep1Time = "~30 sec",
    promoTeaserStep1Cost = { freeQuota, unitCost ->
        "$freeQuota free per account, then $unitCost cr"
    },
    freeQuotaAnnotationRemaining = { remaining, total ->
        "Free — $remaining of $total free annotations remaining"
    },
    freeQuotaAnnotationExhausted = { total ->
        "Free annotations exhausted ($total of $total used). " +
            "Next one is paid."
    },
    freeQuotaCoverRemaining = { remaining, total ->
        "Free — $remaining of $total free cover sets remaining"
    },
    freeQuotaCoverExhausted = { total ->
        "Free cover sets exhausted ($total of $total used). " +
            "Next generation is paid."
    },
    freeQuotaTeaserRemaining = { remaining, total ->
        "Free — $remaining of $total free scene previews remaining"
    },
    freeQuotaTeaserExhausted = { total ->
        "Free scene previews exhausted ($total of $total used). " +
            "Next preview is paid."
    },
    freeQuotaHighlightRemaining = { remaining, total ->
        "$remaining of $total free checks left"
    },
    freeQuotaHighlightExhausted = { _ ->
        "free checks used up"
    },
    promoTeaserStep2Title = "Render the video",
    promoTeaserStep2Time = "~1–2 min",
    promoTeaserStepsTotalLabel = "Total for the 5-sec teaser",
    promoHookVariantsLabel = "Headline:",
    promoStatusReady = "Ready to generate",
    promoStatusLocked = "Unlocks after the deep analysis",
    tag19thCentury = "19th century",
    tagAcademy = "academy",
    tagAction = "action",
    tagAntiquity = "antiquity",
    tagApocalypse = "apocalypse",
    tagAscension = "ascension",
    tagCastlePalace = "castle/palace",
    tagChosenOne = "chosen one",
    tagComingOfAge = "coming of age",
    tagCozy = "cosy",
    tagCyberpunk = "cyberpunk",
    tagDark = "dark",
    tagDarkLord = "dark lord",
    tagDemons = "demons",
    tagDetective = "detective",
    tagDragons = "dragons",
    tagDystopia = "dystopia",
    tagElves = "elves",
    tagEnemiesToLovers = "enemies to lovers",
    tagFakeMarriage = "fake marriage",
    tagFantasy = "fantasy",
    tagForbiddenLove = "forbidden love",
    tagFuture = "future",
    tagHeist = "heist",
    tagInvestigation = "investigation",
    tagIronic = "ironic",
    tagIsekai = "isekai",
    tagIsekaiFemale = "female isekai",
    tagLitrpg = "LitRPG",
    tagLoneliness = "loneliness",
    tagLossGrief = "loss/grief",
    tagMagic = "magic",
    tagMedieval = "medieval",
    tagMercenary = "mercenary",
    tagNostalgic = "nostalgic",
    tagPoliticsIntrigue = "politics/intrigue",
    tagPostApocalypse = "post-apocalypse",
    tagPowerTyranny = "power/tyranny",
    tagPrison = "prison",
    tagRedemption = "redemption",
    tagRevenge = "revenge",
    tagRomance = "romance",
    tagSchoolAcademy = "school/academy",
    tagSciFi = "sci-fi",
    tagSeaShip = "sea/ship",
    tagSecondChance = "second chance",
    tagSpace = "space",
    tagSport = "sport",
    tagSpy = "spy",
    tagSteampunk = "steampunk",
    tagThriller = "thriller",
    tagTournament = "tournament",
    tagUssr = "USSR",
    tagVampires = "vampires",
    tagWar = "war",
    tagWerewolves = "werewolves",
    tagWw2 = "WWII",
    tagZombies = "zombies",
    textQuality = "Text Quality",
    tipArcVolatility = "Magnitude of emotional swings through the book. Low — even tone; high — sharp shifts between dark and light. Match to your story's intent, not «better/worse».",
    tipAuthorStance = "Authorial certainty markers: of course, obviously, naturally, no doubt. Low — author stays out of the way; high — pushes conclusions. To raise — cut overt opinion injections.",
    tipCefrRange = "Vocabulary spread across CEFR difficulty levels (A1-C2 + off-list). Low — narrow band; high — text uses easy and hard words evenly.",
    tipCleanSyntax = "Average distance between a word and the word it depends on syntactically. Low — short, easy-to-parse sentences; high — long winding clauses. To simplify — break sentences shorter.",
    tipCoherence = "How strongly neighbouring sentences pull on the same theme. Low — text jumps; high — single fabric. To raise — repeat key words and use linking phrases between sentences.",
    tipComplexity = "Reading difficulty: paragraph and sentence length, punctuation, dialogue share. Low — easy read; high — dense and demanding. Match to your audience, not «better/worse».",
    tipConcreteness = "Concreteness of content words: physical objects vs abstractions. Low — abstract, head-only writing; high — tangible imagery. To raise — pick concrete nouns over abstractions.",
    tipDialogue = "Share of direct speech. Low — narration-heavy; high — almost all dialogue. Genre-driven.",
    tipDirectPov = "Density of perception verbs with a pronoun subject: «he saw», «she felt», «I heard». Low — direct narration; high — events filtered through a viewpoint. Lower reads as more immediate.",
    tipDirectness = "Action directness: «decided» beats «made a decision», «visited» beats «paid a visit». Low — many «do an X» constructions; high — direct verbs. To raise — swap «do an X» for the verb itself.",
    tipExpressiveness = "Emotional punctuation: exclamations, questions, ellipses. Low — calm tone; high — frequent emotional spikes. Genre-driven.",
    tipFillers = "Filler words: well, like, you know, sort of. Low — many fillers; high — clean prose. To raise — search for these words and trim.",
    tipLiveliness = "Freedom from bureaucratic language: utilise, in connection with, hereinafter, said party. Low — corporate-speak; high — lively prose. To raise — rewrite «in connection with» → «about», «utilise» → «use».",
    tipMorality = "Share of moral-themed vocabulary: conscience, justice, betrayal, forgiveness. Low — moral theme absent; high — ethical dilemmas central. Match to your story's intent.",
    tipNoNominalisation = "Density of «zombie nouns» — nouns ending in -tion, -sion, -ment, -ity, -ness, -ism. Low — verbs dominate; high — nouns smother action. To raise — rewrite «made a decision» → «decided».",
    tipNoRepetition = "Share of content words echoing within a 50-word window. Low — same words on every line; high — lexically varied. To raise — swap repeats for synonyms or pronouns.",
    tipOriginality = "Freedom from cliches: «her heart skipped a beat», «pitch black». Low — lots of stock phrases; high — fresh wording. To raise — rewrite worn-out turns of phrase in your own words.",
    tipPace = "Narrative speed. Low — slow exposition; high — short paragraphs, dialogue and action. Genre-driven.",
    tipPathos = "Elevated lexicon: greatness, valor, sacred, triumph. Low — grounded language; high — pompous tone. Genre-driven (epic / historical ↔ literary).",
    tipPhrasalVerbs = "Density of phrasal verbs: figure out, make up, look into. Low — formal register; high — conversational. Match to your tone.",
    tipPrecision = "Restraint with intensifiers: very, extremely, absolutely, incredibly. Low — text propped up by them; high — precise word choice without crutches. To raise — replace «very big» with one strong word («huge»).",
    tipPrefix = "Tip: ",
    tipRareVocabulary = "Share of words outside the CEFR A1-C2 list: proper nouns, specialised terms, neologisms. Low — everyday vocabulary; high — specialised or invented. Genre-driven.",
    tipRegisterShift = "Difference in vocabulary level between narration and dialogue. Low — both registers blend; high — characters speak noticeably simpler or harder than the narrator. High — intentional contrast.",
    tipRhythm = "Variety in sentence length. Low — all the same length (monotonous); high — short and long alternate. To raise — vary your rhythm.",
    tipSensory = "Sensory words: sounds, smells, touches, tastes. Low — text lives in heads and dialogue; high — all five senses engaged. To raise — add concrete sensory detail.",
    tipStageDirections = "Density of small body actions: nodded, shrugged, glanced. Low — invisible bodies; high — characters constantly moving. Too many feels mechanical.",
    tipVerbStrength = "Verb strength: «sprinted», «slammed» beat «was running»; concrete verbs beat «is/was/has». Low — weak forms abound; high — active verbs throughout. To raise — replace «was» with an action verb.",
    tipVividness = "Imagery: metaphors, sensory detail, concrete pictures. Low — dry description; high — vivid in the reader's head. To raise — replace abstractions with concrete images.",
    tipVocabulary = "Vocabulary richness (MTLD index). Low — words repeating; high — synonyms in active use. To raise — replace repeats with synonyms.",
    tipVocabularyAge = "Mean age at which a reader first learns the words used (in years). Low — childhood/everyday vocabulary; high — abstract or specialised. Audience-driven.",
    tipVoices = "Distinctness of different characters' speech. Low — everyone sounds the same; high — each has a recognisable voice. To raise — give each character their own vocabulary, rhythm, pet phrases.",
    topUpDebug = "+10 credits (debug)",
    topUpTitle = "Top up balance",
    signupBonusTitle = "Sign in — claim your welcome bonus",
    signupBonusBody = { annotations, covers, checks ->
        "and free generations: annotation ×$annotations, covers ×$covers, text check ×$checks"
    },
    verificationBannerText = { email -> if (email.isNotEmpty()) "Verify $email to unlock credits — a confirmation link has been sent to your inbox." else "Verify your email to unlock credits — a confirmation link has been sent to your inbox." },
    verificationRequired = "Verify your email first — check your inbox",
    verificationResend = "Resend link",
    verificationConfirm = "I've verified",
    verificationEmailSent = "Verification email sent — check your inbox",
    verificationEmailError = "Failed to send email, please try again later",
    verificationCooldown = { s -> "You can resend in $s seconds" },
    verificationCooldownButton = { s -> "Resend in ${s}s" },
    verificationNotYet = "Email not verified yet — check your inbox",
    verificationAlreadyDone = "Email is already verified",
    unsupportedLanguageMessage = "Couldn't detect the language. The file may not have extracted as readable text — see the snippet below.",
    unsupportedLanguageSampleHeader = "Extracted text:",
    uploadFormats = ".fb2, .epub, .txt, .html, .docx, .pdf",
    uploadPrivacy = "We don't share your text with third parties, don't use it for any other purpose, and claim no rights to the generated material.",
    uploadScope = "Designed for a single coherent work of any size. Story collections and books made of several unrelated novellas yield mixed results.",
    uploadTitle = "Upload your manuscript",
    wherePrefix = "Where: ",
    words = "Words",
    wordsAndChange = "words / change",
)
